// May this visitor see the RaisedBeef Index prices? (The index lock, 2026-10-13.)
//
// The same answer the database gives — rb_site_my_billing's level, read by
// decideAccess() — for the surfaces that cannot use
// useSiteAccess: the prerendered homepage and the strip, which must not import
// the Supabase client (it touches localStorage at import and throws under
// Node). The session comes from the ticker library's hand-off instead.
//
// ⚠️ THE SCREEN FOLLOWS, THE SERVER DECIDES. A wrong answer here shows a lock
// to someone who could have seen the price, or an empty card to someone who
// could not; it never shows a price. The readers refuse on their own.

import { useEffect, useState } from "react";

import { onTickerAuthChange, rpcValue, tickerAuthToken } from "@/lib/rbCattleTicker";
import { decideAccess, type Access, type Level } from "@/lib/siteAccess";
import { tickerAuthSettled } from "@/lib/tickerAuth";

interface Billing { level?: Level | null; trial_ends_at?: string | null }
const isBilling = (v: unknown): v is Billing => typeof v === "object" && v !== null && !Array.isArray(v);

/** One plan read per sign-in, shared by every card on the screen — Clara
 *  Light draws a dozen at once and each would otherwise ask on its own. */
let memo: { token: string | null; at: number; p: Promise<Access> } | null = null;

export function readIndexAccess(now: Date = new Date()): Promise<Access> {
  return tickerAuthSettled().then(() => {
    const token = tickerAuthToken();
    if (memo && memo.token === token && now.getTime() - memo.at < 60_000) return memo.p;
    memo = { token, at: now.getTime(), p: askIndexAccess(token, now) };
    return memo.p;
  });
}

async function askIndexAccess(token: string | null, now: Date): Promise<Access> {
  if (!token) {
    return decideAccess({ loading: false, signedIn: false, level: null, trialEndsAt: null }, now);
  }
  const b = await rpcValue("rb_site_my_billing", {}, isBilling);
  return decideAccess({
    loading: false,
    signedIn: true,
    level: b?.level ?? null,
    trialEndsAt: b?.trial_ends_at ?? null,
  }, now);
}

/** "loading" on the server and the first client render, so hydration matches. */
export function useIndexAccess(): Access {
  const [access, setAccess] = useState<Access>({ state: "loading" });
  useEffect(() => {
    let live = true;
    const read = () => { void readIndexAccess().then((a) => { if (live) setAccess(a); }); };
    read();
    // ⚠️ ASK AGAIN WHEN THE SESSION LANDS LATE. The first read waits at most
    // two seconds; a member whose session arrives after that was read as a
    // stranger and kept the locked cards until a reload (2026-10-13).
    const off = onTickerAuthChange(read);
    return () => { live = false; off(); };
  }, []);
  return access;
}

/** The button under a locked panel, by who is looking (index-lock mock). */
export function lockButton(state: Access["state"]): string {
  if (state === "expired") return "Subscribe";
  return "Start your free 30 days";
}
